import { Collapse, Divider, TableRow, useTheme } from '@material-ui/core';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import { calcInventoryItemDetailRoute, calcProductCatalogDetailRoute } from 'app/main/inventoryManagement/routes';
import { calcPatientDetailRoute } from 'app/main/patients/calcPatientDetailRoute';
import { ExpandLessOrMoreIcon } from 'framework/components/ExpandLessOrMoreIcon';
import { ColorOnHoverIconButton } from 'framework/components/buttons/ColorOnHoverIconButton';
import { TextLinkButton } from 'framework/components/buttons/TextLinkButton';
import { useThemeColor } from 'framework/components/color/useThemeColor';
import { StatusIcon } from 'framework/components/icons/StatusIcon';
import { useStateBoolean } from 'framework/hooks/useStateBool';
import { TableCellX } from 'framework/table/TableCellX';
import { TableDetailsRowX } from 'framework/table/TableDetailsRowX';
import { formatDate } from 'framework/utils/date/formatDate';
import { ILoan, LoanProcessStatus } from 'gen/ApiClient';
import { useLocalization } from 'localization/useLocalization';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { calcLoanDetailRoute } from '../../ProcessesRoutes';
import { LoanStatusChip } from '../LoanStatusChip';
import { LoanListItemStatusIcon } from './LoanListItemStatusIcon';
import { LoanProcessStatusToColorXRecord } from './LoanProcessStatusToColorXRecord';

const MinorFontSize = 13;

interface IProps {
	item: ILoan;
	isExpanded: boolean;
	toggle: VoidFunction;
}

export const LoanTableRow = ({ item, isExpanded, toggle }: IProps) => {
	const strings = useLocalization();
	const color = useMemo(() => LoanProcessStatusToColorXRecord[item.status as LoanProcessStatus], [item]);
	const tColor = useThemeColor(color);
	const theme = useTheme();
	const { push } = useHistory();
	const [isHovered, enter, leave] = useStateBoolean(false);

	const onViewDetails = () => push(calcLoanDetailRoute(item.id));

	return (
		<React.Fragment>
			<TableRow
				onMouseEnter={enter}
				onMouseLeave={leave}
				style={{ backgroundColor: isHovered ? theme.palette.action.hover : 'unset' }}>
				<TableCellX>
					<div className='df-row gap-4'>
						<LoanListItemStatusIcon
							item={item}
							style={{ fontSize: 22, color: tColor }}
						/>
						<div className='df-col gap-2'>
							<TextLinkButton
								underline
								style={{ fontSize: 14 }}
								onClick={onViewDetails}>
								{item.reference}
							</TextLinkButton>
							<LoanStatusChip
								item={item}
								color={color}
								style={{ marginRight: 'auto' }}
							/>
						</div>
					</div>
				</TableCellX>
				<TableCellX>
					<div className='df-col gap-2'>
						<span>{formatDate(item.startDate)}</span>
						{Boolean(item.actualStopDate) === false && (
							<div className='df-row-ac gap-2'>
								{item.hasEndWarning && (
									<StatusIcon
										status='warning'
										fontSize='small'
									/>
								)}
								<span style={{ fontSize: MinorFontSize, color: theme.palette.text.secondary }}>{`${strings.expectedEndDate}: ${formatDate(
									item.stopDate
								)}`}</span>
							</div>
						)}
						{Boolean(item.actualStopDate) === true && (
							<div className='df-row-ac gap-2'>
								<span style={{ fontSize: MinorFontSize, color: theme.palette.text.secondary }}>{`${strings.endDate}: ${formatDate(item.actualStopDate)}`}</span>
							</div>
						)}
					</div>
				</TableCellX>
				<TableCellX>
					<TextLinkButton
						style={{ fontSize: MinorFontSize }}
						underline
						onClick={() => push(calcPatientDetailRoute(item.forPatientId))}>
						{item.forPatientIdentifier}
					</TextLinkButton>
				</TableCellX>
				<TableCellX align='right'>
					<div className='df-row-ac jc-e'>
						<span style={{ fontSize: MinorFontSize, fontWeight: 'unset' }}>
							{`# ${item.countItems.toString()} ${
								item.countItems === 1 || item.countItems === -1 ? strings.article.toLowerCase() : strings.articles.toLowerCase()
							}`}
						</span>
						<ColorOnHoverIconButton
							edge='end'
							size='small'
							color='primary'
							onClick={toggle}>
							<ExpandLessOrMoreIcon isExpanded={isExpanded} />
						</ColorOnHoverIconButton>
						<ColorOnHoverIconButton
							size='small'
							edge='end'
							color='primary'
							onClick={onViewDetails}>
							<VisibilityOutlinedIcon />
						</ColorOnHoverIconButton>
					</div>
				</TableCellX>
			</TableRow>
			<TableDetailsRowX
				colSpan={4}
				onMouseEnter={enter}
				onMouseLeave={leave}
				style={{ backgroundColor: isHovered ? theme.palette.action.hover : 'unset' }}>
				<Collapse in={isExpanded}>
					<div
						className='df-col'
						style={{ paddingBottom: 12 }}>
						<Divider
							variant='inset'
							style={{ marginTop: 8, marginBottom: 4 }}
						/>
						{item.lines.map(line => (
							<div
								className='df-row-ab'
								key={line.id}
								style={{ fontSize: MinorFontSize, gap: 4 }}>
								<span>{`${line.quantity.toString()} x`}</span>
								<TextLinkButton
									underline
									style={{ fontSize: MinorFontSize }}
									onClick={() => {
										if (line.inventoryItemId) {
											push(calcInventoryItemDetailRoute(line.inventoryItemId));
										} else {
											push(calcProductCatalogDetailRoute(line.productCatalogItemId));
										}
									}}>
									{line.description}
								</TextLinkButton>
							</div>
						))}
					</div>
				</Collapse>
			</TableDetailsRowX>
		</React.Fragment>
	);
};
